import classNames from 'classnames';
import React, { ReactElement, ReactNode, useContext, useState } from 'react';
import { uniqueId } from '@lmig/lmds-helpers/dist/uniqueId';
import { Breakpoints } from '@lmig/lmds-helpers/dist/types/breakpoints';
import { FloatingHeaderContext } from '../context/FloatingHeaderContext';
import { FloatingHeaderBaseProps } from '../types';
import { ParentItemAccordion } from './parentItem/ParentItemAccordion';
import { ParentItemDropdown } from './parentItem/ParentItemDropdown';
import { useMinBreakpoint } from '../hooks/useMinBreakpoint';

export interface HeaderParentItem extends FloatingHeaderBaseProps {
  /**
   * Label to appear within the navigation menu item.
   * If inside an overlay, this will display as the `Accordion` label.
   * If not within an overlay, this will display inside the button that toggles the menu.
   */
  labelVisual: ReactNode;
  /**
   * The header parent item will be shown at or above this breakpoint. If this prop is not set, then the header item will always be shown.
   */
  showBreakpoint?: Breakpoints;
  /**
   * The href attribute for the mega menu title link
   */
  href?: String | undefined;
  /**
   * The onClick attribute for the mega menu title link
   */
  onClick: Function | undefined;
}

/**
 * Use this component if you want to open a menu of options.
 * If only a link, consider using the `HeaderItem` component.
 */
export const HeaderParentItem = ({
  id,
  className,
  children,
  showBreakpoint,
  labelVisual,
  href,
  onClick,
  ...otherProps
}: HeaderParentItem): ReactElement => {
  /**
   * We create a uniqueID to use to toggle the activeMenuID within the top level `FloatingHeader` to know which dropdown is currently open.
   * */
  const { showHorizontalMenu } = useContext(FloatingHeaderContext);
  const breakpointFinal = showBreakpoint || 'always';
  const showItem = useMinBreakpoint(breakpointFinal);

  const [uniqueID] = useState(() => id || uniqueId('FloatingHeader-item'));
  const containerClasses = classNames(
    'lmig-FloatingHeader-item',
    'lmig-FloatingHeader-item--parent',
    { 'lmig-FloatingHeader-item-hidden': !showItem },
    className,
  );

  const allProps = {
    id: uniqueID,
    className: containerClasses,
    labelVisual,
    href,
    onClick,
    ...otherProps,
  };

  /**
   * If the current breakpoint is included in the overlayBreakpoints, then we display the menus in an Accordion.
   * The `HeaderNavigation` will be hidden until the `overlayIsOpen` === true
   */
  const ParentComponent = showHorizontalMenu ? ParentItemDropdown : ParentItemAccordion;

  return <ParentComponent {...allProps}>{children}</ParentComponent>;
};
